import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Spin,
  message,
  Descriptions,
  Table,
  Divider,
  Empty,
  Image,
  Button,
} from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../api/api";
import jsPDF from "jspdf"; // Import jsPDF
import html2pdf from "html2pdf.js"; // Import html2pdf.js
import EditPassengersModal from "../components/EditPassengersModal";

const labelStyle = {
  fontWeight: "bold",
  color: "#555",
  minWidth: 150,
  display: "inline-block",
};

const sectionTitleStyle = {
  fontSize: 18,
  fontWeight: "bold",
  marginTop: 40,
  marginBottom: 16,
  borderBottom: "1px solid #ddd",
  paddingBottom: 4,
};

function VacationOrderDetailPage() {
  const navigate = useNavigate();
  const { order_id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [originalXml, setOriginalXml] = useState(null);

  const [originalPassengers, setOriginalPassengers] = useState([]);
  const [originalFlights, setOriginalFlights] = useState([]);

  const [editPassengerModalVisible, setEditPassengerModalVisible] =
    useState(false);
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const userRole = userInfo.role_id;

  const normalizeArray = (input) => {
    if (!input) return [];
    return Array.isArray(input) ? input : [input];
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          message.error("User not authenticated.");
          return;
        }

        const res = await fetch(
          `${API_BASE_URL}vacationPackageOrder&order_id=${order_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const json = await res.json();
        if (json.success && json.data) {
          const formatted = mapApiToOrder(json.data);
          setOrder(formatted);
          setOriginalPassengers(formatted.passengers);
          setOriginalFlights(formatted.flights);
        } else {
          message.error("Failed to load order data.");
        }
      } catch (err) {
        console.error(err);
        message.error("Error loading order data.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [order_id]);

  function formatDate(yyyymmdd) {
    if (!yyyymmdd || yyyymmdd.length !== 8) return "";

    const year = parseInt(yyyymmdd.slice(0, 4), 10);
    const month = parseInt(yyyymmdd.slice(4, 6), 10) - 1; // Month is 0-based
    const day = parseInt(yyyymmdd.slice(6, 8), 10);

    const date = new Date(year, month, day);
    if (isNaN(date)) return "";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  const handlePassengersUpdate = async (updated) => {
    setOrder((prev) => ({ ...prev, passengers: updated }));
    setEditPassengerModalVisible(false);

    // Format birth_date to yyyymmdd
    const formattedPassengers = updated.map((p) => ({
      ...p,
      birth_date: p.birth_date.replace(/-/g, ""), // "1990-05-20" → "19900520"
    }));

    try {
      const token = localStorage.getItem("token");
      console.log("formatted data", formattedPassengers);
      const res = await fetch(`${API_BASE_URL}vacationPackageOrder`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          order_id: order.order_id,
          passengers: formattedPassengers,
        }),
      });

      const json = await res.json();
      if (json.success) {
        message.success("Passenger info updated successfully");
        setOriginalPassengers(updated);
      } else {
        message.error("Failed to update passengers");
      }
    } catch (err) {
      console.error(err);
      message.error("Error updating passengers");
    }
  };

  const handleToggleStatus = async () => {
    const token = localStorage.getItem("token");
    const newStatus = order.status === 1 ? 0 : 1;

    try {
      const res = await fetch(`${API_BASE_URL}vacationPackageOrder`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          order_id: order.order_id,
          status: newStatus,
        }),
      });

      const json = await res.json();
      if (json.success) {
        message.success("Order status updated.");
        setOrder((prev) => ({ ...prev, status: newStatus }));
      } else {
        message.error("Failed to update status.");
      }
    } catch (err) {
      console.error(err);
      message.error("Error updating order status.");
    }
  };

  /*   const buildUpdatedXml = (originalXml, updatedPassengers) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(originalXml, "application/xml");

    const passengersNode = xmlDoc.querySelector("PASSENGERS > PASSENGER");

    if (!passengersNode) {
      console.error("Could not find PASSENGERS > PASSENGER node in XML");
      return originalXml; // fallback to original
    }

    // Remove all existing <item> nodes
    while (passengersNode.firstChild) {
      passengersNode.removeChild(passengersNode.firstChild);
    }

    updatedPassengers.forEach((p) => {
      const item = xmlDoc.createElement("item");
      item.innerHTML = `
      <TITLE>${p.title}</TITLE>
      <FIRST_NAME>${p.first_name}</FIRST_NAME>
      <MIDDLE_NAME>${p.middle_name || ""}</MIDDLE_NAME>
      <LAST_NAME>${p.last_name}</LAST_NAME>
      <BIRTH_DATE>${p.birth_date.replace(/-/g, "")}</BIRTH_DATE>
      <AGE>${p.age}</AGE>
    `;
      passengersNode.appendChild(item);
    });

    const serializer = new XMLSerializer();
    return serializer.serializeToString(xmlDoc);
  }; */

  const parseValue = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const mapApiToOrder = (data) => {
    const passengers = normalizeArray(
      data.booking_info?.PASSENGERS?.PASSENGER?.item
    ).map((p) => ({
      title: p.TITLE,
      first_name: p.FIRST_NAME,
      middle_name: p.MIDDLE_NAME,
      last_name: p.LAST_NAME,
      birth_date:
        p.BIRTH_DATE.slice(0, 4) +
        "-" +
        p.BIRTH_DATE.slice(4, 6) +
        "-" +
        p.BIRTH_DATE.slice(6, 8),
      age: p.AGE,
    }));

    const flights = normalizeArray(
      data.results?.ITINERARIES?.ITINERARY?.item
    ).map((f) => ({
      carrier: f.OPERATING_AIRLINE_NAME,
      flight: f.TTSFLIGHTNO,
      from: f.AIRPORT_DEP,
      to: f.AIRPORT_ARR,
      dep_date: formatDate(f.DATE_DEP),
      dep_time: f.TIME_DEP.slice(0, 2) + ":" + f.TIME_DEP.slice(2, 4),
      arr_date: formatDate(f.DATE_ARR),
      arr_time: f.TIME_ARR.slice(0, 2) + ":" + f.TIME_ARR.slice(2, 4),
      class: f.CLASS_CATEGORY_NAME,
    }));

    const payments = normalizeArray(
      data.booking_info?.PAYMENTS?.PAYMENT?.item
    ).map((p) => {
      const cardNum = p.NUMBER || "";
      const maskedNumber =
        cardNum.length >= 8
          ? `${cardNum.slice(0, 4)} **** **** ${cardNum.slice(-4)}`
          : cardNum;

      return {
        number: maskedNumber,
        type: p.TYPE + "." + p.CC_TYPE,
        amount: parseValue(p.AMOUNT),
      };
    });

    const hotel = {
      name: data.hotel_name,
      image: data.hotel_img,
      check_in: formatDate(data.results.PACKAGE.CHECKIN_DATE),
      check_out: formatDate(data.results.PACKAGE.CHECKOUT_DATE),
      room_type: data.results.PACKAGE.ROOM_DESCRIPTION,
      occupation: data.results.PACKAGE.OCCUPANCY_DESC,
    };

    // Pricing calculation using ADULT and NON_ADULTS
    const adult = data.results?.PASSENGERS?.ADULT;
    const nonAdults = data.results?.PASSENGERS?.NON_ADULTS?.NON_ADULT || [];

    const adultCount = parseValue(adult?.NB);
    const adultPrice = parseValue(adult?.PRICE);
    const adultTax = parseValue(adult?.TAX);

    const nonAdultList = Array.isArray(nonAdults) ? nonAdults : [nonAdults];

    const totalNonAdultPrice = nonAdultList.reduce(
      (sum, pax) => sum + parseValue(pax?.BASE_PRICE),
      0
    );
    const totalNonAdultTax = nonAdultList.reduce(
      (sum, pax) => sum + parseValue(pax?.TAX),
      0
    );

    const calculatedPrice = adultCount * adultPrice + totalNonAdultPrice;
    const calculatedTax = adultCount * adultTax + totalNonAdultTax;
    const pricing = {
      price: calculatedPrice,
      taxes: calculatedTax,
      total: calculatedPrice + calculatedTax,
      comm: parseValue(data.results.COMMISSION),
      gst_c: parseValue(data.results.GST_COMM),
      hst_c: parseValue(data.results.HST_COMM),
      net: parseValue(data.results.PRICENET),
    };

    const paymentItems = normalizeArray(
      data.booking_info?.PAYMENTS?.PAYMENT?.item
    );

    const totalPaid = paymentItems.reduce((sum, p) => {
      const amount = parseFloat(p.AMOUNT);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    return {
      order_id: data.order_id,
      tour_operator: data.tour_operator,
      status: data.status,
      booking_date: data.add_time,
      departure_date: data.departure_date,
      return_date: data.return_date,
      final_pay_date: formatDate(data.results.BALANCE_DATE),
      total_price: parseValue(data.total_trip_cost),
      total_paid: totalPaid > 0 ? totalPaid : null,
      min_deposit: parseValue(data.results.MINIMUM_DEPOSIT),
      commission: parseValue(data.results.COMMISSION),
      total_net: parseValue(data.results.PRICENET),
      agent_name: data.booking_info.AGENT_NAME,
      phone:
        data.booking_info.PHONE_HOME.slice(0, 3) +
        "-" +
        data.booking_info.PHONE_HOME.slice(3, 6) +
        "-" +
        data.booking_info.PHONE_HOME.slice(6, 10),
      email: data.booking_info.EMAIL,

      passengers,
      hotel_details: hotel,
      room_number: data.room_number,
      flights,
      pricing,
      payments,
      terms: data.results.MANDATORY_INFOS,
      payout: data.payout,
    };
  };

  const renderRow = (label, value) => (
    <div style={{ marginBottom: 8 }}>
      <span style={labelStyle}>{label}:</span> {value}
    </div>
  );

  const passengerColumns = [
    { title: "Title", dataIndex: "title" },
    { title: "First Name", dataIndex: "first_name" },
    { title: "Middle Name", dataIndex: "middle_name" },
    { title: "Last Name", dataIndex: "last_name" },
    { title: "Birth Date", dataIndex: "birth_date" },
    { title: "Age", dataIndex: "age" },
  ];

  const flightColumns = [
    { title: "Carrier", dataIndex: "carrier" },
    { title: "Flight", dataIndex: "flight" },
    { title: "From", dataIndex: "from" },
    { title: "To", dataIndex: "to" },
    { title: "Departure Date", dataIndex: "dep_date" },
    { title: "Departure Time", dataIndex: "dep_time" },
    { title: "Arrival Date", dataIndex: "arr_date" },
    { title: "Arrival Time", dataIndex: "arr_time" },
    { title: "Class", dataIndex: "class" },
  ];

  const paymentColumns = [
    { title: "Type", dataIndex: "type" },
    { title: "Number", dataIndex: "number" },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amt) => `$${amt.toFixed(2)}`,
    },
  ];

  const fetchImageAsBase64 = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handlePrint = async () => {
    const element = document.getElementById("order-details");

    // Convert hotel image to Base64
    const hotelImageBase64 = await fetchImageAsBase64(
      order.hotel_details.image
    );

    // Replace the image src with the Base64 data URL
    const imageElement = document.querySelector(
      'img[src="' + order.hotel_details.image + '"]'
    );
    if (imageElement) {
      imageElement.src = hotelImageBase64;
    }

    const options = {
      filename: `order_details_${order_id}.pdf`, // Dynamically include order_id
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf().from(element).set(options).save();
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", paddingTop: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ textAlign: "center", paddingTop: 50 }}>
        <Empty description="Order not found" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }} id="order-details">
      <div style={{ position: "relative" }}>
        <Button
          type="link"
          onClick={() => navigate(-1)}
          style={{
            marginBottom: 20,
            backgroundColor: "#f0f5ff", // Lighter blue or gray for back button
            color: "#1890ff", // Use blue text to maintain the link style
            border: "1px solid #1890ff", // Subtle border for contrast
            padding: "6px 16px",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "bold", // Makes the text more noticeable
          }}
        >
          ← Back
        </Button>

        <Button
          type="primary"
          onClick={handlePrint}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            padding: "10px 20px",
            fontSize: "16px",
            fontWeight: "bold",
            borderRadius: "8px",
            backgroundColor: "rgba(255, 72, 0, 0.1)", // Soft orange background
            color: "#ff4800", // Vibrant orange text color
            border: "1px solid #ff4800", // Orange border for consistency
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Soft shadow for depth
            transition: "background-color 0.3s ease, transform 0.3s ease",
          }}
          icon={<ExportOutlined />}
          onMouseOver={(e) =>
            (e.target.style.backgroundColor = "rgba(255, 72, 0, 0.2)")
          } // Slightly darker orange on hover
          onMouseOut={(e) =>
            (e.target.style.backgroundColor = "rgba(255, 72, 0, 0.1)")
          } // Original soft orange color
        >
          Export to PDF
        </Button>
      </div>
      <h2>Order Detail: {order.order_id}</h2>

      <div style={sectionTitleStyle}>Order Information</div>
      {renderRow("Supplier", order.tour_operator)}
      {renderRow("Booking Time", order.booking_date)}
      {renderRow("Departure Date", order.departure_date)}
      {renderRow("Return Date", order.return_date)}
      {renderRow("Final Pay Date", order.final_pay_date)}
      {renderRow("Total Price", `$${order.total_price}`)}
      {renderRow("Total Paid", `$${order.total_paid}`)}
      {renderRow("Minimum Deposit", `$${order.min_deposit}`)}
      {userRole !== 3 && renderRow("Commission", `$${order.commission}`)}
      {userRole === 3 && renderRow("Commission", `$${order.payout}`)}
      {userRole !== 3 && renderRow("Total Net", `$${order.total_net}`)}
      {/*       {renderRow("Agent Name", order.agent_name)} */}
      {renderRow("Phone", order.phone)}
      {renderRow("Email", order.email)}

      {/* Order Status Row with Color and Toggle Button */}
      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>Status:</span>{" "}
        <span
          style={{
            color: order.status === 1 ? "green" : "red",
            fontWeight: "bold",
            marginRight: 16,
          }}
        >
          {order.status === 1 ? "Confirmed" : "Cancelled"}
        </span>
        {userRole !== 3 && (
          <Button
            size="small"
            style={{
              borderColor: "#d9d9d9",
              color: "#555",
              padding: "2px 10px",
              borderRadius: "4px",
              fontWeight: "500",
            }}
            onClick={handleToggleStatus}
          >
            {order.status === 1 ? "Mark as Cancelled" : "Mark as Confirmed"}
          </Button>
        )}
      </div>

      <div style={sectionTitleStyle}>Passengers Information</div>
      {userRole !== 3 && (
        <Button type="link" onClick={() => setEditPassengerModalVisible(true)}>
          Edit Passengers
        </Button>
      )}
      <Table
        dataSource={order.passengers}
        columns={passengerColumns}
        rowKey={(row, idx) => idx}
        pagination={false}
        bordered
        size="small"
        rowClassName={(record, index) => {
          const orig = originalPassengers[index];
          if (!orig) return "highlight-row"; // New added
          const changed = Object.keys(record).some(
            (key) => record[key] !== orig[key]
          );
          return changed ? "highlight-row" : "";
        }}
      />

      <div style={sectionTitleStyle}>Hotel Details</div>
      {order.hotel_details ? (
        <>
          {Array.from({ length: Number(order.room_number) || 0 }).map(
            (_, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 24,
                  marginBottom: 24,
                }}
              >
                <Image
                  width={300}
                  src={order.hotel_details.image}
                  alt="Hotel"
                />
                <div>
                  {renderRow("Hotel Name", order.hotel_details.name)}
                  {renderRow("Check-in Date", order.hotel_details.check_in)}
                  {renderRow("Check-out Date", order.hotel_details.check_out)}
                  {renderRow("Room Type", order.hotel_details.room_type)}
                  {renderRow("Occupation", order.hotel_details.occupation)}
                  {Number(order.room_number) > 1 && (
                    <div
                      style={{
                        marginTop: 8,
                        fontStyle: "italic",
                        color: "#999",
                      }}
                    >
                      Room #{index + 1}
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </>
      ) : (
        <Empty description="No hotel data" />
      )}

      <div style={sectionTitleStyle}>Flight Itinerary</div>
      <Table
        dataSource={order.flights}
        columns={flightColumns}
        rowKey={(row, idx) => idx}
        pagination={false}
        bordered
        size="small"
      />

      {userRole !== 3 && (
        <div>
          <div style={sectionTitleStyle}>Price Details</div>
          <Table
            dataSource={[order.pricing]}
            columns={[
              {
                title: "Price",
                dataIndex: "price",
                render: (val) => `$${val.toFixed(2)}`,
              },
              {
                title: "Taxes",
                dataIndex: "taxes",
                render: (val) => `$${val.toFixed(2)}`,
              },
              {
                title: "Total",
                dataIndex: "total",
                render: (val) => `$${val.toFixed(2)}`,
              },
              {
                title: "Comm.",
                dataIndex: "comm",
                render: (val) => `$${val.toFixed(2)}`,
              },
              {
                title: "GST/C",
                dataIndex: "gst_c",
                render: (val) => `$${val.toFixed(2)}`,
              },
              {
                title: "HST/C",
                dataIndex: "hst_c",
                render: (val) => `$${val.toFixed(2)}`,
              },
              {
                title: "Net",
                dataIndex: "net",
                render: (val) => `$${val.toFixed(2)}`,
              },
            ]}
            pagination={false}
            bordered
            size="small"
          />
        </div>
      )}

      <div style={sectionTitleStyle}>Payments Information</div>
      <Table
        dataSource={order.payments}
        columns={paymentColumns}
        rowKey={(row, idx) => idx}
        pagination={false}
        bordered
        size="small"
        summary={() => {
          const total = order.payments.reduce((sum, p) => sum + p.amount, 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <strong>Total Paid</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}>
                <strong>${total.toFixed(2)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />

      <div style={sectionTitleStyle}>Terms and Conditions</div>
      <p>{order.terms}</p>

      <EditPassengersModal
        visible={editPassengerModalVisible}
        passengers={order.passengers}
        onSave={handlePassengersUpdate}
        onCancel={() => setEditPassengerModalVisible(false)}
      />
    </div>
  );
}

export default VacationOrderDetailPage;
